import CartTotalDisplay, { CartTotalDisplayStyles } from "@bevco/Overrides/Components/CartTotalDisplay";
import useSticky from "@bevco/Utilities/Hooks/customUseStickyCartTotal";
import { colorStrings } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import CartSaveOrderButton from "@insite/content-library/Widgets/Cart/CartSaveOrderButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import isMobile from "ismobilejs";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    // Verndale Customization
    const accountSettings = getSettingsCollection(state).accountSettings;
    // END Verndale

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
		isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        // Verndale Customization
        isSubmitForQuoteButtonDisabled:
            isCartLoading || isUpdatingCart || (cart?.cartLines?.every(o => o.quoteRequired === false) ?? false),
        allowGuestRFQCheckout: accountSettings.allowGuestCheckout,
        rfqRequestQuoteGuestPageUrl: getPageLinkByPageType(state, "RfqRequestQuoteGuestPage")?.url,
        isGuest: state.context.session?.isGuest,
        // END Verndale Customization
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    // Verndale Customization
    cartTotalWrapper?: InjectableCss;
    twoButtonModal?: TwoButtonModalStyles;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: fixed;
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            margin-top: 10px;
                            /* static — not inherit — so sticky parent position:fixed does not break the button */
                            position: static;
                            left: auto;
                            bottom: auto;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            /* static — not inherit — so sticky parent position:fixed does not break the button */
            position: static;
            left: auto;
            bottom: auto;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            /* static — not inherit — so sticky parent position:fixed does not break the button */
            position: static;
            left: auto;
            bottom: auto;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            /* static — not inherit — so sticky parent position:fixed does not break the button */
            position: static;
            left: auto;
            bottom: auto;
        `,
    },
    submitRequisitionButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            /* static — not inherit — so sticky parent position:fixed does not break the button */
            position: static;
            left: auto;
            bottom: auto;
        `,
    },

    // Verndale Customization
    cartTotalWrapper: {
        css: css`
            padding: 20px;
            position: relative;
        `,
    },

    cartTotal: {
        container: {
            css: css`
                padding: 0;
            `,
        },
    },
};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    // Verndale Customization
    allowGuestRFQCheckout,
    rfqRequestQuoteGuestPageUrl,
    isGuest,
}: // END Verndale Customization
Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [isMobilebroswer, setIsMobile] = useState(false);

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
				history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                ); 
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if ((!cart?.isAuthenticated || isGuest) && allowGuestRFQCheckout && rfqRequestQuoteGuestPageUrl) {
            history.push(rfqRequestQuoteGuestPageUrl);
            return;
        }

        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for Quote");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    // Verndale Customization - check is there is no quote required products in the cart
    const hasNoQuoteRequiredProducts = cart?.cartLines?.every(cartLine => !cartLine.quoteRequired);
 

    useSticky('stickyWrapper', // name of the main wrapper to be sticky
        ['cartTotal_checkout_button',
        'cartTotal_submitQuote_button',
        'cancel_punchout_button',
        '[data-test-selector="cart-save-Order"]']  // all buttons on the view total
        ,80 // offset
        ,60); // top

    return (
        <StyledWrapper {...styles.cartTotalWrapper} id="stickyWrapper">
            <CartTotalDisplay
                showTaxAndShipping={showTaxAndShipping}
                extendedStyles={styles.cartTotal}
                isLoading={isLoading}
                isCartEmpty={isCartEmpty}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
            />
            {fields.includeActions !== false && (
                <>
                    <Button
                        {...styles.checkoutButton}
                        onClick={checkoutHandler}
                        id="cartTotal_checkout_button"
                        disabled={
                            isCartCheckoutDisabled ||
                            hasOnlyQuoteRequiredProducts ||
                            cart?.properties.overWeight === "True"
                        } // Verndale Customization
                        data-test-selector="cartTotal_checkout"
                    >
                        {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                    </Button>
                    {isPunchOutOrder && (
                        <Button
                            {...styles.cancelPunchOutButton}
                            disabled={isPunchoutButtonDisabled}
                            onClick={cancelPunchOutHandler}                            
                            id="cancel_punchout_button"
                        >
                            {translate("Cancel PunchOut")}
                        </Button>
                    )}
                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                        extendedStyles={styles.twoButtonModal}
                    ></TwoButtonModal>
                </>
            )}
            {fields.includeActions !== false && canSubmitForQuote && (
                <Button
                    {...styles.submitQuoteButton}
                    disabled={isSubmitForQuoteButtonDisabled || hasNoQuoteRequiredProducts} // Verndale Customization
                    onClick={submitForQuoteClickHandler}
                    data-test-selector="cartTotal_submitQuote"
                    id="cartTotal_submitQuote_button"
                >
                    {submitForQuoteLabel}
                </Button>
            )}
            {fields.includeActions !== false && (
                <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} data-test-selector="cart-save-Order"/>
            )}
            {fields.includeActions !== false && canSubmitRequisition && (
                <Button
                    {...styles.submitRequisitionButton}
                    disabled={isSubmitRequisitionButtonDisabled}
                    onClick={submitRequisitionClickHandler}
                    data-test-selector="cartTotal_submitRequisition"                    
                    id="cartTotal_submitQuote_button"
                >
                    {translate("Submit Requisition")}
                </Button>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
